<script setup>
import { computed, useId, useSlots } from "vue";

import InfoTooltip from "./InfoTooltip.vue";

const props = defineProps({
  label: { type: String, default: "" },
  desc: { type: String, default: "" },
  info: { type: String, default: "" },
  error: { type: String, default: "" },
  id: { type: String, default: "" },
  inlineError: { type: Boolean, default: false },
});

const slots = useSlots();
const descriptionId = useId();
const errorId = useId();

// The full `aria-describedby` value for the wrapped control, exposed through the
// default slot. Includes an id only when its element actually renders, so
// consumers can bind it directly without re-deriving these conditions.
const describedBy = computed(() => {
  const ids = [];
  if (props.error) ids.push(errorId);
  if (props.info || slots["label-info"]) ids.push(descriptionId);
  return ids.length ? ids.join(" ") : undefined;
});
</script>

<template>
  <div class="form-field">
    <div v-if="label || $slots.label || info || $slots['label-info']" class="label-row">
      <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
      <label v-if="label || $slots.label" :for="id || undefined" class="label">
        <slot name="label">{{ label }}</slot>
      </label>
      <InfoTooltip
        v-if="info"
        :id="$slots['label-info'] ? undefined : descriptionId"
        :text="info"
      />
      <slot :id="descriptionId" name="label-info" />
    </div>
    <slot :described-by />
    <p v-if="error" :id="errorId" class="error" :class="{ inline: inlineError }">{{ error }}</p>
    <p v-else-if="desc" class="description">{{ desc }}</p>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.form-field {
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  .label-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  .label {
    @include typography.label("semi-bold");

    color: c.$text-color;
    letter-spacing: 0.04em;
  }

  .error {
    @include typography.extra-small("regular");

    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 1px;

    // Matches .description, so swapping the two never changes the row height.
    line-height: 1.3;
    color: c.$brand-danger;
    pointer-events: none;

    &.inline {
      position: static;
      margin-top: 4px;
      pointer-events: auto;
    }
  }

  .description {
    @include typography.extra-small("regular");

    color: c.$text-subtle;
    margin: 4px 0 0;
    line-height: 1.3;
  }
}
</style>
